import { useState } from "react";
import clsx from "clsx";
import { useGameStore } from "../store/useGameStore";
import { CLASS_INFO, getHeroImagePath } from "../utils/assets";
import type { HeroClass, HeroGender } from "../types";

const CLASSES: HeroClass[] = ["WARRIOR", "ARCHER", "MAGE"];

export function CharacterCreationScreen() {
  const { user, createHero } = useGameStore();
  const defaultName = user?.first_name ?? user?.username ?? "";
  const [heroName, setHeroName] = useState(defaultName);
  const [gender, setGender] = useState<HeroGender>("m");
  const [selected, setSelected] = useState<HeroClass | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!selected) return;
    const trimmed = heroName.trim();
    if (trimmed.length < 2) {
      setError("Name must be at least 2 characters");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await createHero(selected, gender, trimmed);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create hero");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-kingdom-dark flex flex-col px-4 py-6 safe-bottom">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold font-game text-gold-400">Create your hero</h1>
        <p className="text-gray-400 text-sm mt-1">Choose a name, gender and class. Class cannot be changed later.</p>
      </div>

      <label className="block max-w-md mx-auto w-full mb-4">
        <span className="text-xs text-gray-400 uppercase tracking-wide">Hero name</span>
        <input
          type="text"
          value={heroName}
          maxLength={20}
          onChange={(e) => setHeroName(e.target.value)}
          className="mt-1 w-full rounded-xl bg-kingdom-purple border border-kingdom-mid px-4 py-3 text-white text-center font-semibold"
          placeholder="Your hero name"
        />
      </label>

      <div className="flex justify-center gap-2 mb-6">
        {(["m", "w"] as HeroGender[]).map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => setGender(g)}
            className={clsx(
              "px-5 py-2 rounded-xl text-sm font-bold border transition-all",
              gender === g
                ? "bg-kingdom-accent border-kingdom-glow text-white"
                : "bg-kingdom-purple border-kingdom-mid text-gray-400",
            )}
          >
            {g === "m" ? "♂ Male" : "♀ Female"}
          </button>
        ))}
      </div>

      <div className="flex-1 flex flex-col gap-3 max-w-md mx-auto w-full">
        {CLASSES.map((heroClass) => {
          const info = CLASS_INFO[heroClass];
          const isSelected = selected === heroClass;
          return (
            <button
              key={heroClass}
              type="button"
              onClick={() => setSelected(heroClass)}
              className={clsx(
                "flex items-center gap-4 p-3 rounded-2xl border-2 text-left transition-all",
                isSelected
                  ? "border-kingdom-glow bg-kingdom-purple shadow-lg shadow-kingdom-glow/20"
                  : "border-kingdom-mid bg-kingdom-purple/60 hover:border-kingdom-light",
              )}
            >
              <img
                src={getHeroImagePath(heroClass, gender)}
                alt={info.title}
                className="w-20 h-24 object-contain object-bottom shrink-0"
              />
              <div className="min-w-0">
                <div className="font-bold text-white font-game">{info.title}</div>
                <div className="text-kingdom-glow text-xs font-medium">{info.role}</div>
                <div className="text-gray-400 text-xs mt-1 leading-snug">{info.description}</div>
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <p className="text-rose-400 text-sm text-center mt-4">{error}</p>
      )}

      <button
        type="button"
        onClick={handleCreate}
        disabled={!selected || loading}
        className={clsx(
          "mt-6 w-full max-w-md mx-auto py-3 rounded-xl font-bold text-lg transition-all",
          selected && !loading
            ? "bg-gradient-to-r from-kingdom-accent to-kingdom-glow text-white"
            : "bg-gray-800 text-gray-500 cursor-not-allowed",
        )}
      >
        {loading ? "Creating..." : selected ? `Play as ${CLASS_INFO[selected].title}` : "Select a class"}
      </button>
    </div>
  );
}
